/*eslint-disable @typescript-eslint/explicit-module-boundary-types*/

import FlattenUtil from 'flat';
import { MerchantOnboardingInitRequest, MerchantResponseDto, OnboardingRequest, SessionPrefillData } from "@/api/paymentApi";
import { AppDefaults } from "@/app/utils/common/constants";
import { OnboardingSectionData } from "@/store/modules/onboarding/onboardingTypes";
import { copyFromApiResponse, copyFromErrorResponse, prefillField } from "@/store/storeMappers";
import { validateAndGenerateError, validateAndSetValid } from "@/store/validationFromStore.js";
import { BusinessAddressFields } from "./incrementalOnboardingbusinessAddress";

export enum EssentialFormField {
    COMPANY_NAME ="companyName",
    DOING_BUSINESS_AS ="doingBusinessAs",
    TYPE_OF_BUSINESS ="typeOfBusiness",
    OWNERSHIP_TYPE ="ownershipType",
    BUSINESS_COUNTRY ="businessCountry"
}

export const essentialsFormDefaultState: OnboardingSectionData = {
    api: "createMerchant",
    errors: {},
    dirty: false,
    isPrefilled: false,
    data: {
        [EssentialFormField.COMPANY_NAME]: {
            apiPath: "business_info.name",
            value: null,
            valid: false,
        },
        [EssentialFormField.DOING_BUSINESS_AS]: {
            apiPath: "business_info.doing_business_as",
            value: null,
            valid: false,
        },
        [EssentialFormField.TYPE_OF_BUSINESS]: {
            apiPath: "business_info.type",
            value: null,
            valid: false,
        },
        [EssentialFormField.OWNERSHIP_TYPE]: {
            apiPath: "business_info.ownership_type",
            value: null,
            valid: false,
        },
        [EssentialFormField.BUSINESS_COUNTRY]: {
            apiPath: "business_info.address.country",
            value: AppDefaults.country,
            valid: true,
            defaultValue: AppDefaults.country,
        },
    },
};

const getters = {
    isValid: (state) => {
        return Object.keys(state.data).every(key => state.data[key].valid);
    },
    getErrors: (state) => {
        return state.errors
    },
    isPrefilled: (state) => {
        return state.isPrefilled
    },
    getCountry: s => s.data[EssentialFormField.BUSINESS_COUNTRY].value,
    getTypeOfBusiness: s => s.data[EssentialFormField.TYPE_OF_BUSINESS].value,
    getField: (s) => {
        return (field: EssentialFormField) => s.data[field].value 
    },
    totalInvalidFields: (s) => {
        return Object.keys(s.data).filter(key => s.data[key].valid === false).length
    },
    totalValidFields: (s) => {
        return Object.keys(s.data).filter(key => s.data[key].valid === true).length
    },
    isDirty: (s) => {
        return s.dirty
    },
    getIncrementalOnboardingData: (s, _getters, _rootState, rootGetters): MerchantOnboardingInitRequest => {
        return {
            requestType: OnboardingRequest.RequestTypeEnum.INITMERCHANTONBOARDING,
            merchant_agreement_accepted: rootGetters["agreementAcceptance/getSubMerchantAgreementAcceptance"],
            merchant_agreement_ip_address: rootGetters["agreementAcceptance/getMerchantAgreementAcceptanceIpAddress"],
            merchant_agreement_timestamp: rootGetters["agreementAcceptance/getMerchantAgreementAcceptanceTimestamp"],
            merchant_agreement_user_agent: rootGetters["agreementAcceptance/getMerchantAgreementAcceptanceUserAgent"],
            name: s.data[EssentialFormField.COMPANY_NAME].value,
            doing_business_as: s.data[EssentialFormField.DOING_BUSINESS_AS].value,
            business_type: s.data[EssentialFormField.TYPE_OF_BUSINESS].value,
            ownership_type: s.data[EssentialFormField.OWNERSHIP_TYPE].value,
            business_country: s.data[EssentialFormField.BUSINESS_COUNTRY].value
        };
    }
}

const actions = {
    resetErrors({commit}) {
        commit('resetErrors');
    },
    setErrors({commit}, {key, errors}) {
        commit('setErrors', {key, errors});
    },
    validate({commit, state, rootGetters}) {
        commit('resetErrors')
        return validateAndGenerateError(commit, state.api, rootGetters, rootGetters["essentials/getCountry"], state.data)
    },
    validateField({commit, state, rootGetters}, key) {
        validateAndSetValid(commit, essentialsFormDefaultState.api, state.data, rootGetters["essentials/getCountry"], key)
    },
    validateAllFields({state, dispatch}) {
        Object.keys(state.data).forEach(key => dispatch('validateField', key))
    },
    updateFieldFromEvent({commit, dispatch}, {key, event}: {key: EssentialFormField, event: any}) {
        commit('updateField', {key, value: event.target.value})
        if (key === EssentialFormField.BUSINESS_COUNTRY) {
            dispatch('incrementalOnboardingBusinessAddress/updateFieldFromValue', 
                {key: BusinessAddressFields.COUNTRY, value: event.target.value}, {root: true})
            dispatch('businessAddress/updateCountry', event.target.value, {root: true})
        }
        commit('setDirty')
        dispatch('validateField', key)
    },
    updateFieldFromValue({commit, dispatch}, {key, value}: {key: EssentialFormField, value: any}) {
        commit('updateField', {key, value})
        commit('setDirty')
        dispatch('validateField', key)
    },
    setDirty({commit}) {
        commit('setDirty')
    },
    resetDirty({commit}) {
        commit('resetDirty')
    },
    copyFromFieldErrorsResponse({commit}, subErrors) {
        commit('copyFromFieldErrorsResponse', subErrors)
    },
    copyFromMerchantApiResponse({commit}, merchantApiResponse) {
        commit('copyFromMerchantApiResponse', merchantApiResponse)
    },
    copyFromPrefillData({commit}, {prefillData, merchantResponse}) {
        commit('copyFromPrefillData', {prefillData, merchantResponse})
    }
};

const mutations = {
    resetErrors(state): void {
        state.errors = {}
    },
    setErrors(state, {key, errors}): void {
        state.errors[key] = errors;
    },
    setInvalid(state, key) {
        state.data[key].valid = false;
    },
    setValid(state, key) {
        state.data[key].valid = true;
    },
    updateField(state, {key, value}) {
        state.data[key].value = value;
        delete state.errors[key];
    },
    setDirty(state) {
        state.dirty = true;
    },
    resetDirty(state) {
        state.dirty = false;
    },
    copyFromFieldErrorsResponse: function (state, subErrors): void {
        state.errors = copyFromErrorResponse(subErrors, state.data)
    },
    copyFromMerchantApiResponse(state, merchantApiResponse): void {
        state.data = copyFromApiResponse(merchantApiResponse, state.data);
    },
    copyFromPrefillData(state, {prefillData, merchantResponse}: {prefillData: SessionPrefillData, merchantResponse: MerchantResponseDto}): void {
        const flatApiResponse = FlattenUtil(merchantResponse || {});
        let prefillApplied = false;
        
        prefillApplied = prefillField(state, EssentialFormField.COMPANY_NAME, flatApiResponse, prefillData.organization?.name) || prefillApplied;
        prefillApplied = prefillField(state, EssentialFormField.DOING_BUSINESS_AS, flatApiResponse, prefillData.organization?.name) || prefillApplied;
        prefillApplied = prefillField(state, EssentialFormField.BUSINESS_COUNTRY, flatApiResponse, prefillData.organization?.address?.country) || prefillApplied;

        state.isPrefilled = prefillApplied;
    }
};

export const essentials = {
    namespaced: true,
    state: essentialsFormDefaultState,
    getters,
    actions,
    mutations,
};

